import { useEffect, useState } from "react";
import {
  fetchInstructionAckChallenge,
  verifyInstructionAckPasskey,
  type InstructionAckChallengeResponse
} from "../api/client";
import type { BuyerFundingFlowInstructions, BuyerFundingFlowInstructionAck } from "../flows/buyerFunding/types";
import { INSTRUCTION_ACK_PASSKEY_LIMITATION } from "../domain/instructionAckPasskey";

type Props = {
  fileCode: string;
  instructions: BuyerFundingFlowInstructions;
  ackChecked: boolean;
  onAckCheckedChange: (checked: boolean) => void;
  onSubmit: (ack: BuyerFundingFlowInstructionAck) => void;
};

function passkeySupported(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential !== "undefined";
}

function rpIdForHost(): string {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return host;
  return host;
}

export function InstructionAckPanel({
  fileCode,
  instructions,
  ackChecked,
  onAckCheckedChange,
  onSubmit
}: Props) {
  const [challenge, setChallenge] = useState<InstructionAckChallengeResponse | null>(null);
  const [passkeyDone, setPasskeyDone] = useState(false);
  const [passkeyError, setPasskeyError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!passkeySupported()) return;
    void fetchInstructionAckChallenge(fileCode, instructions.versionLabel, instructions.publishedAt)
      .then(setChallenge)
      .catch(() => setChallenge(null));
  }, [fileCode, instructions.publishedAt, instructions.versionLabel]);

  async function handlePasskey() {
    if (!challenge || !passkeySupported()) return;
    setPasskeyError("");
    setBusy(true);
    try {
      const credential = (await navigator.credentials.create({
        publicKey: {
          rp: { name: challenge.rpName, id: challenge.rpId },
          user: {
            id: Uint8Array.from(new TextEncoder().encode(`buyer-${fileCode}`)),
            name: `${fileCode}-buyer`,
            displayName: "Buyer"
          },
          challenge: Uint8Array.from(
            atob(challenge.challenge.replace(/-/g, "+").replace(/_/g, "/")),
            (c) => c.charCodeAt(0)
          ),
          pubKeyCredParams: [{ type: "public-key", alg: -7 }],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            residentKey: "preferred",
            userVerification: "preferred"
          },
          timeout: 60_000,
          attestation: "none"
        }
      })) as PublicKeyCredential | null;

      if (!credential || !(credential.response instanceof AuthenticatorAttestationResponse)) {
        throw new Error("Passkey was not created.");
      }

      const clientDataJSON = btoa(
        String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))
      )
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");

      const verified = await verifyInstructionAckPasskey({
        fileCode,
        versionLabel: instructions.versionLabel,
        publishedAt: instructions.publishedAt,
        credentialId: credential.id,
        clientDataJSON,
        expectedChallenge: challenge.challenge
      });

      if (!verified.verified) {
        throw new Error(verified.error || "Passkey verification failed.");
      }

      setPasskeyDone(true);
      onAckCheckedChange(true);
      onSubmit({
        method: "passkey",
        instructionVersion: instructions.versionLabel,
        instructionPublishedAt: instructions.publishedAt,
        acknowledgedAt: new Date().toISOString(),
        passkeyCredentialId: verified.credentialId || credential.id,
        limitation: verified.limitation || INSTRUCTION_ACK_PASSKEY_LIMITATION
      });
    } catch (error) {
      setPasskeyError(error instanceof Error ? error.message : "Passkey could not be recorded.");
    } finally {
      setBusy(false);
    }
  }

  function handleCheckboxSubmit() {
    onSubmit({
      method: "checkbox",
      instructionVersion: instructions.versionLabel,
      instructionPublishedAt: instructions.publishedAt,
      acknowledgedAt: new Date().toISOString(),
      limitation:
        "Buyer checkbox acknowledgment for the instruction version shown. This is not identity verification or wire approval."
    });
  }

  const showPasskey = passkeySupported() && challenge && rpIdForHost() === challenge.rpId;

  return (
    <section className="panel" data-testid="instruction-ack-panel">
      <p className="text-sm text-[#596158]">Version {instructions.versionLabel}</p>

      {showPasskey ? (
        <div className="mt-3 rounded-lg border border-[#d8d2c4] bg-[#fffdf8] p-4">
          <button
            className="button-primary"
            type="button"
            data-testid="instruction-ack-passkey"
            disabled={busy || passkeyDone}
            onClick={() => void handlePasskey()}
          >
            {passkeyDone ? "Passkey recorded" : busy ? "Waiting for passkey…" : "Use passkey"}
          </button>
          {passkeyError ? <p className="mt-2 text-sm text-[#8a2d10]">{passkeyError}</p> : null}
        </div>
      ) : null}

      <label className="mt-4 flex items-start gap-3 rounded-md border border-[#d8d2c4] bg-[#fffdf8] p-3">
        <input
          data-testid="buyer-ack-checkbox"
          type="checkbox"
          checked={ackChecked}
          disabled={passkeyDone}
          onChange={(e) => onAckCheckedChange(e.target.checked)}
        />
        <span className="text-sm">
          I reviewed version {instructions.versionLabel} for file {fileCode}. The office decides whether funds may be
          relied on.
        </span>
      </label>
      {!passkeyDone ? (
        <button
          className="button-primary mt-3"
          data-testid="buyer-acknowledge"
          disabled={!ackChecked || busy}
          type="button"
          onClick={handleCheckboxSubmit}
        >
          Record acknowledgment
        </button>
      ) : null}
    </section>
  );
}
